import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);
  return (
    <footer className="bg-primary text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              {settings?.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt={settings.business_name} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold text-xl">BE</span>
                  </div>
                  <span className="text-xl font-semibold">BrightEdge</span>
                </>
              )}
            </div>
            <p className="text-white/80 text-sm">
              Professional cleaning services for homes and businesses in Perth, Western Australia.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-white/80 hover:text-white transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-white/80 hover:text-white transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-white/80 hover:text-white transition-colors text-sm">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-white/80 hover:text-white transition-colors text-sm">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/80 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2 text-sm">
                <Phone size={16} />
                <a href={`tel:${settings?.business_phone || '+610415134053'}`} className="text-white/80 hover:text-white transition-colors">
                  {settings?.business_phone || '+610415134053'}
                </a>
              </li>
              <li className="flex items-center space-x-2 text-sm">
                <Mail size={16} />
                <a
                  href={`mailto:${settings?.business_email || 'contact@brightedgecleaningperth.com.au'}`}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {settings?.business_email || 'contact@brightedgecleaningperth.com.au'}
                </a>
              </li>
              <li className="flex items-start space-x-2 text-sm">
                <MapPin size={16} className="mt-1" />
                <span className="text-white/80">{settings?.business_address || 'Perth, WA, Australia'}</span>
              </li>
            </ul>
          </div>

          {/* Social & Legal */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Follow Us</h3>
            <div className="flex space-x-4 mb-6">
              <a
                href={settings?.facebook_url || 'https://facebook.com'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-link"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href={settings?.instagram_url || 'https://instagram.com'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-link"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
            <div className="space-y-2">
              <Link to="/privacy" className="block text-white/80 hover:text-white transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="block text-white/80 hover:text-white transition-colors text-sm">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} {settings?.business_name || 'BrightEdge Cleaning Perth'}. All rights reserved.
          </p>
          <p className="text-white/40 text-xs mt-2">
            Made by <a href="https://www.linkedin.com/in/fardinsrv" className="hover:text-white transition-colors">fardinsrv</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
